import { Box, Container, Grid, Link, Paper, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import { Button, createSelectField } from '@roc/ui';
import { observer } from 'mobx-react';
import { useCallback, useEffect } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { useSecureLinkAchFormStore } from '../hooks/useSecureLinkAchFormStore';
import { isPlaidOAuthReturn } from '../stores/secureLinkAchFormStore';
import { withdrawalDayOptions } from '../utils/constants';
import { AchConnectionGuide } from './achConnectionGuide/AchConnectionGuide';
import { useAchConnectionGuide } from './achConnectionGuide/useAchConnectionGuide';
import { LoanDetailsCard } from './loanDetailsCard';

const useStyles = makeStyles((theme: Theme) => ({
  button: {
    fontWeight: 'normal',
    padding: theme.spacing(2, 8),
  },
  connected: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    gap: theme.spacing(2),
  },
  notice: {
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.warning.light,
  },
}));

const capitalize = (value?: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : '');

/**
 * The bank step through Plaid (public ACH link): connect the bank first, then the name on the account and the payment
 * day, then Continue (one submit). Bank name, numbers and account type come from Plaid; nothing is saved before
 * Continue. Handles the return from a bank's OAuth login (re-opens Link with the stored token).
 */
export const ConnectBankStep = observer(() => {
  const classes = useStyles();
  const { achFormStore } = useSecureLinkAchFormStore();
  const formStore = achFormStore.plaidBankInformationFormStore;
  const connected = achFormStore.connectedAccount;
  const oauthReturn = isPlaidOAuthReturn();
  // Explains Plaid's screens; it opens only from the link under the button, never by itself.
  const guide = useAchConnectionGuide('borrower', false);

  useEffect(() => {
    achFormStore.fetchPlaidLinkToken();
  }, [achFormStore]);

  const onSuccess = useCallback(
    (publicToken: string, metadata: any) => achFormStore.onPlaidSuccess(publicToken, metadata),
    [achFormStore]
  );

  const { open, ready } = usePlaidLink({
    token: achFormStore.plaidLinkToken,
    onSuccess,
    receivedRedirectUri: oauthReturn ? window.location.href : undefined,
  });

  // Back from the bank's OAuth login: finish the same Plaid Link session.
  useEffect(() => {
    if (oauthReturn && ready && !achFormStore.connectedAccount) {
      open();
    }
  }, [oauthReturn, ready, open, achFormStore]);

  const connect = () => {
    achFormStore.changeConnectedAccount();
    open();
  };

  return (
    <Container maxWidth={'md'} disableGutters>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Box>
            Once your loan becomes funded, ACH enables easy automated loan payments. Connect the bank account you will use
            to pay your monthly loan payments.
          </Box>
        </Grid>
        {achFormStore.bankStepNotice && (
          <Grid item xs={12}>
            <Paper elevation={0} className={classes.notice}>
              <Typography variant="body2">{achFormStore.bankStepNotice}</Typography>
            </Paper>
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          {!connected ? (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Button
                  testId="connectBank"
                  variant="contained"
                  color="primary"
                  className={classes.button}
                  disabled={!ready || achFormStore.plaidLinkTokenLoading}
                  onClick={connect}
                >
                  Connect your bank account
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Link component="button" variant="body2" data-testid="connectGuideOpen" onClick={guide.show}>
                  How does connecting work?
                </Link>
              </Grid>
              {achFormStore.sessionEndingSoon && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    Your secure session ends in about {Math.ceil(achFormStore.secondsLeft / 60)} minute(s). Connecting
                    your bank can take a few minutes; if the session ends, open the link from your email again.
                  </Typography>
                </Grid>
              )}
              {achFormStore.manualAvailable && (
                <Grid item xs={12}>
                  <Link component="button" variant="body2" onClick={() => achFormStore.chooseManual()}>
                    Can't connect? Enter your bank details manually
                  </Link>
                </Grid>
              )}
            </Grid>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper variant="outlined" className={classes.connected}>
                  <AccountBalanceIcon color="primary" />
                  <Box flexGrow={1}>
                    <Typography variant="subtitle1">{connected.institutionName || 'Your bank'}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {capitalize(connected.subtype)} {connected.mask ? `•••• ${connected.mask}` : ''}
                    </Typography>
                  </Box>
                  <Link component="button" variant="body2" onClick={connect} disabled={!ready}>
                    Change
                  </Link>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                {createSelectField({
                  store: formStore,
                  testId: 'withdrawalDay',
                  fieldName: 'withdrawalDay',
                  label: 'Recurring Monthly Payment Date',
                  tooltip:
                    'Select the day of the month when your payments will be made - this can be any day from the 1st to the 10th',
                  options: withdrawalDayOptions,
                })}
              </Grid>
              <Grid item xs={12}>
                <i>
                  Your account will only be debited after your loan has funded, any applicable interest reserves have
                  been depleted, and your monthly loan payments are due.
                </i>
              </Grid>
            </Grid>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          <LoanDetailsCard />
        </Grid>
        {connected && (
          <Grid item xs={12}>
            <Box display="flex" justifyContent="center">
              <Button
                testId="continue"
                variant="contained"
                color="primary"
                className={classes.button}
                disabled={achFormStore.plaidSubmitting}
                onClick={() => achFormStore.submitPlaid()}
              >
                CONTINUE
              </Button>
            </Box>
          </Grid>
        )}
      </Grid>
      <AchConnectionGuide variant="borrower" open={guide.open} onClose={guide.close} />
    </Container>
  );
});
